import { useEffect, useRef } from 'react';
import { getToken } from 'firebase/messaging';
import { messaging, messagingSupported } from '@/services/firebase';
import { vapidKey, isFirebaseConfigured } from '@/config/firebase.config';
import { useIsAuthenticated } from '@/features/auth/authV2Store';
import { registerDevice } from '@/features/notifications/api';

/**
 * Post-auth FCM device registration (ADR-0007).
 *
 * When the staff web session is authenticated, best-effort:
 * 1. request Notification permission (if not already decided),
 * 2. get an FCM token,
 * 3. PUT /api/v1/me/device { fcm_token, platform: 'web' }.
 *
 * Never uses the legacy `/api/notifications/tokens/register` path.
 * Failures are silent (push is optional); in-app notifications still work.
 */
export function useRegisterWebDevice(): void {
  const isAuthenticated = useIsAuthenticated();
  const attemptedForSession = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      attemptedForSession.current = false;
      return;
    }
    if (attemptedForSession.current) return;
    attemptedForSession.current = true;

    let cancelled = false;

    (async () => {
      if (!isFirebaseConfigured() || !messagingSupported || !messaging) {
        return;
      }
      if (typeof Notification === 'undefined') {
        return;
      }

      try {
        let permission = Notification.permission;
        if (permission === 'default') {
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted' || cancelled) return;

        const token = await getToken(messaging, { vapidKey });
        if (!token || cancelled) return;

        await registerDevice(token);
      } catch (err) {
        // Push registration must never break the authenticated shell.
        console.warn('web device register (PUT /me/device) skipped:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);
}
